import React, { useState } from 'react';
import { ComingSoon } from '../../components/common/ComingSoon';
import { MOCK_QUEUES, QUEUE_ORDER } from '../../mocks';
import { ForgeQueue, ForgeQueueGroup, ForgeQueueHealth } from '../../types';

function worstHealth(a: ForgeQueueHealth, b: ForgeQueueHealth): ForgeQueueHealth {
  const rank: Record<ForgeQueueHealth, number> = { critical: 0, attention: 1, healthy: 2 };
  return rank[a] <= rank[b] ? a : b;
}

function groupQueues(queues: ForgeQueue[]): ForgeQueueGroup[] {
  const map = new Map<string, ForgeQueueGroup>();
  for (const q of queues) {
    const existing = map.get(q.queueName);
    if (existing) {
      existing.totalTasks += q.taskCount;
      existing.totalClaimed += q.claimedCount;
      existing.worstHealth = worstHealth(existing.worstHealth, q.health);
      existing.chains.push(q);
    } else {
      map.set(q.queueName, {
        queueName: q.queueName,
        displayName: q.displayName,
        totalTasks: q.taskCount,
        totalClaimed: q.claimedCount,
        worstHealth: q.health,
        chains: [q],
      });
    }
  }
  return [...map.values()].sort(
    (a, b) => (QUEUE_ORDER[a.queueName] ?? 99) - (QUEUE_ORDER[b.queueName] ?? 99)
  );
}

export const QueueManagementPage: React.FC = () => {
  const groups = groupQueues(MOCK_QUEUES);
  const [enabled, setEnabled] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(groups.map((g) => [g.queueName, true]))
  );

  const toggle = (name: string) => {
    setEnabled((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  return (
    <div className="page-content">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700 }}>Queue Management</h2>
        <button className="btn-secondary">Trigger Regeneration</button>
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th>Queue</th>
            <th>Total Tasks</th>
            <th>Chains</th>
            <th>Status</th>
            <th>Enabled</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <tr key={g.queueName}>
              <td style={{ fontWeight: 600 }}>{g.displayName}</td>
              <td style={{ fontFamily: 'var(--font-mono)' }}>{g.totalTasks}</td>
              <td style={{ fontFamily: 'var(--font-mono)' }}>{g.chains.length}</td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className={`forge-health-dot ${g.worstHealth}`} />
                  <span className="forge-health-label">{g.worstHealth}</span>
                </div>
              </td>
              <td>
                <div
                  className={`toggle-switch ${enabled[g.queueName] ? 'active' : ''}`}
                  onClick={() => toggle(g.queueName)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: 32 }}>
        <ComingSoon
          title="Advanced Queue Configuration"
          description="Queue priorities, auto-regeneration rules, and per-chain scheduling will be available in a future update."
        />
      </div>
    </div>
  );
};
